import React from 'react';
import {
    Box,
    Typography,
    Paper,
    alpha,
    useTheme,
    Popper,
    Fade,
    Grid
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { TimeStamp, CalibrationType } from './types';
import { getReagentColor } from './utils';

interface DeviceInfoTooltipProps {
    box: string;
    iccid?: string;
    modem?: string;
    labels?: Array<{ color: string | null, label: string }>;
    timeStamps?: TimeStamp[];
    comments?: string | null;
    calibrations?: CalibrationType[];
}

const getEventColor = (event: string, theme: any) => {
    const colors = {
        added_to_inventory: {
            bg: theme.palette.success.main,
            text: theme.palette.success.contrastText
        },
        moved_to_service: {
            bg: theme.palette.warning.main,
            text: theme.palette.warning.contrastText
        },
        returned_from_service: {
            bg: theme.palette.info.main,
            text: theme.palette.info.contrastText
        },
        default: {
            bg: theme.palette.grey[500],
            text: theme.palette.getContrastText(theme.palette.grey[500])
        }
    };

    return colors[event as keyof typeof colors] || colors.default;
};

const formatEventDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });
};

const DeviceInfoTooltip: React.FC<DeviceInfoTooltipProps> = ({
    box,
    iccid,
    modem,
    labels,
    timeStamps,
    comments,
    calibrations
}) => {
    const theme = useTheme();
    const [open, setOpen] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const closeTimeoutRef = React.useRef<NodeJS.Timeout>();

    const clearCloseTimeout = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = undefined;
        }
    };

    const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
        clearCloseTimeout();
        setAnchorEl(event.currentTarget);
        setOpen(true);
    };

    const handleMouseLeave = () => {
        closeTimeoutRef.current = setTimeout(() => {
            setOpen(false);
        }, 100);
    };

    React.useEffect(() => {
        return () => clearCloseTimeout();
    }, []);

    return (
        <>
            <Box
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                sx={{
                    position: 'absolute',
                    right: 0,
                    bottom: 0,
                    color: theme.palette.text.secondary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    padding: '6px',
                    cursor: 'pointer',
                    '& .info-icon-wrapper': {
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        backgroundColor: 'transparent',
                        '&:hover': {
                            color: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        }
                    }
                }}
            >
                <Box className="info-icon-wrapper">
                    <InfoIcon sx={{ fontSize: '0.9rem' }} />
                </Box>
            </Box>

            <Popper
                open={open}
                anchorEl={anchorEl}
                placement="top-end"
                transition
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 4],
                        },
                    },
                    {
                        name: 'preventOverflow',
                        options: {
                            padding: 8,
                        },
                    }
                ]}
                sx={{ zIndex: 1300 }}
            >
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={150}>
                        <Paper
                            onMouseEnter={() => {
                                clearCloseTimeout();
                                setOpen(true);
                            }}
                            onMouseLeave={handleMouseLeave}
                            sx={{
                                p: 2,
                                maxWidth: 700,
                                minWidth: 550,
                                bgcolor: theme.name === 'dark'
                                    ? 'rgba(38, 38, 38, 0.98)'
                                    : 'rgba(255, 255, 255, 0.98)',
                                backdropFilter: 'blur(8px)',
                                boxShadow: theme.name === 'dark'
                                    ? '0 4px 23px 0 rgba(0, 0, 0, 0.2)'
                                    : '0 2px 10px 0 rgba(0, 0, 0, 0.1)',
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            }}
                        >
                            <Grid container spacing={2}>
                                {/* Left column */}
                                <Grid item xs={7}>
                                    {/* Device Details Section */}
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', mb: 1, color: theme.palette.primary.main }}>
                                            Device Details
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ minWidth: '70px' }}>
                                                    Box:
                                                </Typography>
                                                <Typography variant="caption" sx={{ 
                                                    fontWeight: 'medium',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {box}
                                                </Typography>
                                            </Box>
                                            {iccid && (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: '70px' }}>
                                                        ICCID:
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ 
                                                        fontWeight: 'medium',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        wordBreak: 'break-all'
                                                    }}>
                                                        {iccid}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {modem && (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: '70px' }}>
                                                        Modem:
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ 
                                                        fontWeight: 'medium',
                                                        color: theme.name === 'dark' ? '#64b5f6' : '#0d47a1',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>
                                                        {modem.replace(/\r|\n/g, '').trim()}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Labels Section */}
                                    {labels && labels.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', mb: 1, color: theme.palette.primary.main }}>
                                                Labels
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {labels.map((label, index) => (
                                                    <Typography
                                                        key={index}
                                                        variant="caption"
                                                        sx={{
                                                            px: 0.75,
                                                            py: 0.25,
                                                            borderRadius: '4px',
                                                            fontSize: '0.65rem',
                                                            backgroundColor: label.color 
                                                                ? alpha(label.color, theme.name === 'dark' ? 0.2 : 0.1)
                                                                : theme.name === 'dark'
                                                                    ? alpha(theme.palette.primary.main, 0.15)
                                                                    : alpha(theme.palette.primary.main, 0.08),
                                                            border: `1px solid ${
                                                                label.color 
                                                                    ? alpha(label.color, 0.3)
                                                                    : alpha(theme.palette.primary.main, 0.2)
                                                            }`,
                                                            color: label.color 
                                                                ? label.color
                                                                : theme.name === 'dark'
                                                                    ? theme.palette.primary.light
                                                                    : theme.palette.primary.main
                                                        }}
                                                    >
                                                        {label.label}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Calibration Section */}
                                    {calibrations && calibrations.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', mb: 1, color: theme.palette.primary.main }}>
                                                Calibrations
                                            </Typography>
                                            <Box sx={{ 
                                                display: 'flex', 
                                                flexWrap: 'wrap',
                                                gap: 0.5,
                                                maxHeight: '170px',
                                                overflowY: 'auto'
                                            }}>
                                                {calibrations.map((calibration, index) => (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            py: 0.5,
                                                            px: 0.75,
                                                            borderRadius: '4px',
                                                            fontSize: '0.65rem',
                                                            backgroundColor: theme.name === 'dark' 
                                                                ? alpha(getReagentColor(calibration.reagent), 0.15)
                                                                : alpha(getReagentColor(calibration.reagent), 0.05),
                                                            border: `1px solid ${
                                                                alpha(getReagentColor(calibration.reagent), 0.3)
                                                            }`,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            width: 'calc(50% - 4px)',
                                                            height: 'fit-content'
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Box 
                                                                sx={{ 
                                                                    width: 8, 
                                                                    height: 8, 
                                                                    borderRadius: '50%', 
                                                                    backgroundColor: getReagentColor(calibration.reagent),
                                                                    boxShadow: `0 0 3px ${getReagentColor(calibration.reagent)}`
                                                                }} 
                                                            />
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    fontWeight: 'bold',
                                                                    fontSize: '0.65rem',
                                                                    color: calibration.reagent === 'NH3' 
                                                                        ? '#a98fd6'
                                                                        : getReagentColor(calibration.reagent),
                                                                    flexGrow: 1
                                                                }}
                                                            >
                                                                {calibration.reagent} #{calibration.calibration_number}
                                                            </Typography>
                                                        </Box>
                                                        
                                                        <Box sx={{ 
                                                            fontSize: '0.6rem', 
                                                            color: 'text.secondary',
                                                            mt: 0.25,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 0
                                                        }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                                                                    Start:
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ fontSize: '0.6rem', ml: 0.5 }}>
                                                                    {new Date(calibration.start_calibration).toLocaleDateString()}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                                                                    End:
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ fontSize: '0.6rem', ml: 0.5 }}>
                                                                    {new Date(calibration.end_calibration).toLocaleDateString()}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Grid>

                                {/* Right column */}
                                <Grid item xs={5}>
                                    {/* Comments Section */}
                                    {comments && (
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', mb: 1, color: theme.palette.primary.main }}>
                                                Comments
                                            </Typography>
                                            <Typography 
                                                variant="caption" 
                                                sx={{ 
                                                    display: 'block',
                                                    color: 'text.secondary',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    backgroundColor: alpha(theme.palette.background.default, 0.5),
                                                    p: 1,
                                                    borderRadius: 1,
                                                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                                    maxHeight: '100px',
                                                    overflowY: 'auto'
                                                }}
                                            >
                                                {comments}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Timeline Section */}
                                    {timeStamps && timeStamps.length > 0 && (
                                        <Box sx={{ mt: comments ? 2 : 0 }}>
                                            <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', mb: 1, color: theme.palette.primary.main }}>
                                                History Timeline
                                            </Typography>
                                            <Box sx={{ 
                                                maxHeight: 150,
                                                overflowY: 'auto',
                                                '&::-webkit-scrollbar': {
                                                    width: '6px',
                                                },
                                                '&::-webkit-scrollbar-thumb': {
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                                    borderRadius: '3px',
                                                },
                                            }}>
                                                {timeStamps.map((stamp, index) => {
                                                    const eventColor = getEventColor(stamp.event, theme);
                                                    return (
                                                        <Box
                                                            key={index}
                                                            sx={{
                                                                position: 'relative',
                                                                mb: 1.5,
                                                                pl: 2,
                                                                '&:before': {
                                                                    content: '""',
                                                                    position: 'absolute',
                                                                    left: '4px',
                                                                    top: 0,
                                                                    bottom: index === timeStamps.length - 1 ? '50%' : 0,
                                                                    width: '1px',
                                                                    backgroundColor: alpha(theme.palette.divider, 0.2),
                                                                }
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    position: 'absolute',
                                                                    left: 0,
                                                                    top: 0,
                                                                    width: 8,
                                                                    height: 8,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: eventColor.bg,
                                                                    border: `1px solid ${theme.palette.background.paper}`,
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    display: 'block',
                                                                    color: theme.palette.text.secondary,
                                                                    fontSize: '0.65rem',
                                                                    mb: 0.25
                                                                }}
                                                            >
                                                                {formatEventDate(stamp.time)}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: eventColor.bg,
                                                                    fontWeight: 'medium',
                                                                    fontSize: '0.7rem'
                                                                }}
                                                            >
                                                                {stamp.event.split('_').join(' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                        </Paper>
                    </Fade>
                )}
            </Popper>
        </>
    );
};

export default DeviceInfoTooltip; 